/* eslint-disable max-len */

import * as React from 'react';
import { StylesProps } from '../../../../theme/jss-types';
import { FormField } from '../../create-consignment.types';
import { formFields } from '../../create-modal.constants';
import { scheduleStepStyles } from '../../single-consignment.styles';
import {
    Form,
    FormInstance,
    Collapse,
    Radio,
    Space,
    Button,
    Empty,
    Row,
} from 'antd';
import {
    CheckCircleFilled,
    HistoryOutlined,
    WarningFilled,
    BulbOutlined,
    PlusCircleOutlined,
    MinusCircleOutlined,
    RightOutlined,
} from '@ant-design/icons';
import { ErrorList, Service } from '../../../../library/Types';
import Loader from '../../../common/Loader';
import {
    getServiceabilityRateTat,
    getServiceabilityRateTatWithPx,
} from '../../../../network/common.api';
import { useTranslation } from 'react-i18next';
import GenericHoc from 'components/common/generic-hoc';
import { HocOptions } from 'components/common/generic-hoc.types';
import { ReduxStore } from 'reducers/redux.types';
import { CustomerPortalConfig } from 'types/master-data-types';
import { convertToAPIBodySaveSingle, getPiecesDetail, getChildClientFallback } from '../single-consignment-helper';
import { formRules } from 'library/constants';
import parcelSvg from '../../../../assets/delivery-parcel.svg';
import { transformPayloadNew } from '../single-cn-new-px';

const { Panel } = Collapse;

const {
    useState,
    useEffect,
    useCallback,
} = React;
interface IProps extends StylesProps<ReturnType<typeof scheduleStepStyles>> {
    form: FormInstance;
    params: any;
    softdataCreationUnifiedFlow: boolean;
    showRiskSurchargeType: boolean;
    allowedConsignmentFormType: any;
    customFieldsList: any;
}

interface PanelRefsMap {
    [key: string]: React.RefObject<HTMLDivElement>;
}

const ServiceTypeSelectorFieldErrorVisibility = (props: IProps) => {
    const {
        classes,
        form,
        params,
        customFieldsList,
        showRiskSurchargeType,
        allowedConsignmentFormType,
        softdataCreationUnifiedFlow,
    } = props;
    const {
        Services,
    } = formFields;
    const { t } = useTranslation();
    const [loading, setLoading] = useState<boolean>(true);
    const [services, setServices] = React.useState<any[]>([]);
    const {
        isCurrentTab,
        getValidationRules,
        setDestinationHolidays,
        setDestinationDeliveryDays,
        piecesData,
        manufacturingCountryList,
        selectedService,
        setSelectedService,
        disabledFields = {},
        isCodFieldPresent,
        isProgrammaticValidationRef,
    } = params;
    const isServiceDisabled = !!disabledFields[Services.key];
    const [activeUAVKeys, setActiveUAVKeys] = useState<string[]>([]);
    const [activeAVKeys, setActiveAVKeys] = useState<string[]>([]);
    const scrollContainerRef = React.useRef<HTMLDivElement>(null);
    const [showBottomShadow, setShowBottomShadow] = useState(false);
    const [showTopShadow, setShowTopShadow] = useState(false);

    const [panelRefs, setPanelRefs] = useState<PanelRefsMap>({});
    const availablePanelRef = React.useRef<HTMLDivElement>(null);
    const unavailablePanelRef = React.useRef<HTMLDivElement>(null);

    const [activeKeys, setActiveKeys] = useState<string[]>([]);

    const handleAlternateOptionsToggle = (key : string, type : string) => {
        if (type === 'UAV') {
            const newActiveKeys = activeUAVKeys.includes(key)
                ? activeUAVKeys.filter((k) => k !== key)
                : [...activeUAVKeys, key];

            setActiveUAVKeys(newActiveKeys);

            if (!activeUAVKeys.includes(key)) {
                setTimeout(() => {
                    if (panelRefs[key] && panelRefs[key].current) {
                        panelRefs[key].current.scrollIntoView({
                            behavior: 'smooth',
                            block: 'nearest',
                        });
                    }
                }, 300);
            }
        } else {
            const newActiveKeys = activeAVKeys.includes(key)
                ? activeAVKeys.filter((k) => k !== key)
                : [...activeAVKeys, key];

            setActiveAVKeys(newActiveKeys);

            if (!activeAVKeys.includes(key)) {
                setTimeout(() => {
                    if (panelRefs[key] && panelRefs[key].current) {
                        panelRefs[key].current.scrollIntoView({
                            behavior: 'smooth',
                            block: 'nearest',
                        });
                    }
                }, 300);
            }
        }
    };

    useEffect(() => {
        if (services.length > 0) {
            const refs: PanelRefsMap = {};
            services.forEach((item : Service) => {
                refs[item.serviceTypeId] = React.createRef<HTMLDivElement>();
            });
            setPanelRefs(refs);
        }
    }, [services]);

    const loadServices = async () => {
        setLoading(true);
        let formData: any;
        // validateFields() re-fires the form's onFieldsChange; flag it as programmatic so the
        // parent's slot-refetch warning is not triggered while loading services on this page.
        if (isProgrammaticValidationRef) {
            isProgrammaticValidationRef.current = true;
        }
        try {
            formData = await form.validateFields();
        } catch {
            formData = form.getFieldsValue();
        } finally {
            if (isProgrammaticValidationRef) {
                isProgrammaticValidationRef.current = false;
            }
        }

        const childClientFallback = getChildClientFallback(formData.childClient);
        let response:any;
        if (softdataCreationUnifiedFlow) {
            formData.piecesDetail = getPiecesDetail(formData, piecesData, manufacturingCountryList);
            response = await getServiceabilityRateTatWithPx(transformPayloadNew({
                ...formData,
                ...childClientFallback,
            }, {
                showRiskSurchargeType,
                isServiceableForm: true,
                isCustomFieldAllowed: true,
                customFieldsList,
                source: 'CUSTOMER_PORTAL_SINGLE_V2',
            }));
        } else {
            formData.piecesDetail = getPiecesDetail(formData, piecesData, manufacturingCountryList);
            response = await getServiceabilityRateTat(convertToAPIBodySaveSingle({
                ...formData,
                ...childClientFallback,
            }, {
                showRiskSurchargeType,
                sendCustomFieldObject: true,
                customFieldsList,
                isCodFieldPresent,
                source: 'CUSTOMER_PORTAL_SINGLE_V2',
            }));
        }
        setServices(response?.data || []);
        const servicesData = response?.data || [];
        const defaultActiveKey = servicesData.filter((item: Service) => item?.serviceability_info?.is_serviceable || item.is_serviceable).length === 0 ? ['1', '2'] : ['1'];
        setActiveKeys(defaultActiveKey);
        setLoading(false);
    };

    const prefillData = () => {
        setSelectedService(form.getFieldValue(Services.key));
    };

    const populateServiceabilityData = (item: Service) => {
        if (setDestinationDeliveryDays) {
            const deliveryDaysOfWeek = item?.serviceability_info?.destination_delivery_days?.split(',').map((day: string) => day.trim().toUpperCase());
            setDestinationDeliveryDays(deliveryDaysOfWeek);
        }
        if (setDestinationHolidays) {
            const destinationHolidaysDates = item?.destination_holidays?.map((holiday: any) => holiday.date);
            setDestinationHolidays(destinationHolidaysDates);
        }
    };

    useEffect(() => {
        if (isCurrentTab) {
            loadServices();
            prefillData();
        }
    }, [isCurrentTab]);

    useEffect(() => {
        if (services.length > 0) {
            const currentServiceType = form.getFieldValue(Services.key);
            if (currentServiceType) {
                const matchedService = services.find(
                    (item: Service) => item.serviceTypeId === currentServiceType,
                );

                if (matchedService) {
                    populateServiceabilityData(matchedService);
                }
            }
        }
    }, [services]);

    const handleSelection = (item: Service) => {
        if (isServiceDisabled) return;
        if (selectedService === item.serviceTypeId) {
            setSelectedService(undefined);
            form.setFieldsValue({
                [Services.key]: undefined,
            });
            if (setDestinationDeliveryDays) {
                setDestinationDeliveryDays('');
            }
            if (setDestinationHolidays) {
                setDestinationHolidays([]);
            }
        } else {
            setSelectedService(item.serviceTypeId);
            form.setFieldsValue({
                [Services.key]: item.serviceTypeId,
            });
            populateServiceabilityData(item);
        }
    };

    const checkScroll = useCallback(() => {
        const container = scrollContainerRef.current;
        if (container) {
            setShowBottomShadow(
                container.scrollHeight > container.clientHeight
                    && container.scrollTop + container.clientHeight < container.scrollHeight,
            );

            setShowTopShadow(container.scrollTop > 0);
        }
    }, []);

    useEffect(() => {
        if (services.length > 0) {
            setTimeout(() => {
                checkScroll();
            }, 100);
        }
    }, [services, checkScroll]);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (container) {
            container.addEventListener('scroll', checkScroll);
            window.addEventListener('resize', checkScroll);
            return () => {
                container.removeEventListener('scroll', checkScroll);
                window.addEventListener('resize', checkScroll);
            };
        }
        return undefined;
    }, [checkScroll]);


    const renderTat = (item: Service) => {
        const showTat = allowedConsignmentFormType.includes('TAT');
        const tat = item?.period || item?.tat || item?.TAT;
        if (!tat || !showTat) {
            return <div className={classes.tatNew} />;
        }
        return (
            <div className={classes.columnNew}>
                <img alt="parcel" className={classes.tatIcon} src={parcelSvg} />
                <div className={classes.tatNew}>
                    {tat.toString().endsWith('Days') ? tat : `${tat} ${t('days')}`}
                </div>
            </div>
        );
    };

    const renderRate = (item: Service) => {
        if (!item.rate?.amount) {
            return null;
        }
        return (
            <div className={classes.finalPriceNew}>
                {item.rate?.currencySymbol ? `${item.rate.currencySymbol} ` : ''}
                {`${item.rate?.amount}`}
            </div>
        );
    };

    const renderDeliveryTime = (item: Service) => {
        const showDeliveryTime = allowedConsignmentFormType.includes('Delivery Time');
        if (!item.deliveryTime || !showDeliveryTime) {
            return (
                <div className={classes.deliveryTimeNewNA}>
                    {t('not_available')}
                </div>
            );
        }
        return (
            <div className={classes.columnNew}>
                <HistoryOutlined />
                <div className={classes.deliveryTimeNew}>
                    {`${t('delivery_by')} ${item?.deliveryTime}`}
                </div>
            </div>
        );
    };

    const renderAlternateOptions = (item: Service, type: string) => {
        if (item.error_list?.length === 0) return null;
        const isExpanded = activeUAVKeys.includes(item.serviceTypeId) || activeAVKeys.includes(item.serviceTypeId);

        return (
            <div
                className={classes.alternateOptions}
                onClick={(e) => {
                    e.stopPropagation();
                    handleAlternateOptionsToggle(item.serviceTypeId, type);
                }}
            >
                {type === 'AV' ? t('alternate_options') : t('unavailable')}
                <div
                    className={classes.arrowIcon}
                    style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
                >
                    <RightOutlined />
                </div>
            </div>
        );
    };

    const getSelectedClass = (item: Service) => {
        const classList = [classes.serviceOption];
        if (selectedService === item.serviceTypeId) {
            classList.push(classes.serviceOptionSelected);
        }
        return classList.join(' ');
    };

    const renderErrorList = (item: Service, type: string) => {
        if (item.error_list?.length === 0) {
            return null;
        }
        return (
            <div className={classes.errorList}>
                <div className={classes.errorHeading}>
                    <BulbOutlined />
                    {type === 'AV' ? `${t('see_why_other_options_for_this_service_are_not_available')} :` : `${t('see_why_unavailable')} :`}
                </div>
                <ul>
                    {item.error_list?.map((itemError: ErrorList) => (
                        <li key={itemError.error_code}>{itemError.error_message}</li>
                    ))}
                </ul>
            </div>
        );
    };

    const formRuleRequired = {
        ...formRules.required,
        message: t('service_type_required'),
    };

    // Available service card: [Radio] [ServiceInfo: name + TAT + delivery] [RightCol: price + alt-options toggle]
    // WarningPanel (error list) rendered below row, state-controlled
    const renderAvailableService = (item : Service) => {
        const showErrorList = (item.error_list ?? []).length > 0;
        const isWarningExpanded = activeAVKeys.includes(item.serviceTypeId);
        return (
            <Row
                className={getSelectedClass(item)}
                ref={panelRefs[item.serviceTypeId]}
            >
                <Row className={classes.radioGroup}>
                    <Radio
                        value={item.serviceTypeId}
                        key={item.serviceTypeId}
                        checked={item.serviceTypeId === selectedService}
                        disabled={isServiceDisabled}
                        onClick={() => handleSelection(item)}
                    >
                        <Row className={classes.serviceCard} justify="space-between" align="middle">
                            {/* ServiceInfo: service name on top, TAT + delivery time below */}
                            <div>
                                <div className={classes.serviceItem}>
                                    {item.serviceType}
                                </div>
                                <div className={classes.serviceInfoRow}>
                                    {renderTat(item)}
                                    {renderDeliveryTime(item)}
                                </div>
                            </div>
                            {/* RightCol: price + alternate options toggle */}
                            <div className={classes.serviceCardRight}>
                                {allowedConsignmentFormType.includes('Rate') ? renderRate(item) : null}
                                {showErrorList && renderAlternateOptions(item, 'AV')}
                            </div>
                        </Row>
                    </Radio>
                </Row>
                {showErrorList && (
                    <div
                        className={classes.errorListCollapse}
                        style={{ maxHeight: isWarningExpanded ? '400px' : '0' }}
                    >
                        {renderErrorList(item, 'AV')}
                    </div>
                )}
            </Row>
        );
    };

    const expandAllUnavailableServices = () => {
        const allUAVKeys = services?.filter((item) => (!item?.serviceability_info?.is_serviceable || !item.is_serviceable)).map((item) => (item.serviceTypeId));
        setActiveUAVKeys(allUAVKeys);
    };

    const collapseAllUnavailableServices = () => {
        setActiveUAVKeys([]);
    };

    const renderExpandAll = () => {
        return (
            <Button
                className={classes.expandAllHeader}
                onClick={expandAllUnavailableServices}
                type="text"
                disabled={activeUAVKeys?.length === services?.filter((item) => (!item?.serviceability_info?.is_serviceable || !item.is_serviceable)).length}
            >
                <PlusCircleOutlined />
                {t('expand_all')}
            </Button>
        );
    };

    const renderCollapseAll = () => {
        return (
            <Button
                className={classes.expandAllHeader}
                onClick={collapseAllUnavailableServices}
                type="text"
                disabled={activeUAVKeys.length === 0}
            >
                <MinusCircleOutlined />
                {t('collapse_all')}
            </Button>
        );
    };

    // Unavailable service card: [ServiceInfo: muted name] [RightCol: "Unavailable" toggle]
    // WarningPanel (error list) rendered below row, state-controlled
    const renderUnavailableService = (item : Service) => {
        const showErrorList = (item.error_list ?? []).length > 0;
        const isWarningExpanded = activeUAVKeys.includes(item.serviceTypeId);
        return (
            <Row
                className={`${classes.serviceOption} ${classes.unavailableCard}`}
                ref={panelRefs[item.serviceTypeId]}
            >
                <div className={classes.unavailableCardHeader}>
                    {/* ServiceInfo: muted service name */}
                    <div className={classes.unavailableServiceName}>
                        {item.serviceType}
                    </div>
                    {/* RightCol: "Unavailable" toggle aligned to the right */}
                    {showErrorList && renderAlternateOptions(item, 'UAV')}
                </div>
                {showErrorList && (
                    <div
                        className={classes.errorListCollapse}
                        style={{ maxHeight: isWarningExpanded ? '400px' : '0' }}
                    >
                        {renderErrorList(item, 'UAV')}
                    </div>
                )}
            </Row>
        );
    };

    const renderAvailableServicesHeader = () => {
        return (
            <div className={classes.servicesHeader}>
                <CheckCircleFilled style={{ color: '#27B479' }} />
                {t('available_services')}
            </div>
        );
    };

    const renderUnavailableServicesHeader = () => {
        return (
            <div className={classes.servicesHeader}>
                <WarningFilled style={{ color: 'red' }} />
                {t('unavailable_services')}
            </div>
        );
    };

    const renderAvailableServices = (field: FormField) => {
        return (
            <div className={classes.servicesRelativeContainer}>
                <div
                    className={`${classes.shadowBase} ${classes.shadowTop}`}
                    style={{ opacity: showTopShadow ? 1 : 0 }}
                />

                <Form.Item
                    name={field.key}
                    initialValue={field.defaultValue}
                    valuePropName={field.valuePropName}
                    className={classes.servicesRelativeContainer}
                    rules={[formRuleRequired, ...getValidationRules(field)]}
                >
                    <Row
                        ref={scrollContainerRef}
                        className={classes.scrollContainerAvailable}
                        onScroll={checkScroll}
                    >
                        {services?.filter((item) => (item?.serviceability_info?.is_serviceable || item.is_serviceable)).map((item) => renderAvailableService(item))}
                    </Row>
                </Form.Item>

                <div
                    className={`${classes.shadowBase} ${classes.shadowBottom}`}
                    style={{ opacity: showBottomShadow ? 1 : 0 }}
                />
            </div>
        );
    };

    const renderUnavailableServices = () => {
        return (
            <Row className={classes.scrollContainerUnavailable}>
                {services?.filter((item) => (!item?.serviceability_info?.is_serviceable || !item.is_serviceable)).map((item) => renderUnavailableService(item))}
            </Row>
        );
    };

    const renderAllOptions = () => {
        return (
            <div className={classes.expandCollapseOptions}>
                {renderCollapseAll()}
                {renderExpandAll()}
            </div>
        );
    };

    const renderServices = () => {
        if (loading) {
            return null;
        }

        const handlePanelChange = (keys: string[]) => {
            setActiveKeys(keys);
            if (keys.includes('2') && !activeKeys.includes('2')) {
                setTimeout(() => {
                    if (unavailablePanelRef.current) {
                        unavailablePanelRef.current.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                        });
                    }
                }, 300);
            }
            if (keys.includes('1') && !activeKeys.includes('1')) {
                setTimeout(() => {
                    if (availablePanelRef.current) {
                        availablePanelRef.current.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                        });
                    }
                }, 300);
            }
        };

        return (
            <Collapse
                activeKey={activeKeys}
                onChange={handlePanelChange}
                bordered={false}
                expandIconPosition="end"
            >
                <Panel
                    header={renderAvailableServicesHeader()}
                    key="1"
                    className={classes.panel}
                >
                    <div ref={availablePanelRef}>
                        {services?.filter((item) => (item?.serviceability_info?.is_serviceable || item.is_serviceable)).length === 0 ? (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        ) : (
                            <Space direction="vertical" style={{ width: '100%' }}>
                                {renderAvailableServices(Services)}
                            </Space>
                        )}
                    </div>
                </Panel>
                <Panel
                    header={renderUnavailableServicesHeader()}
                    key="2"
                    className={classes.panel}
                >
                    <div ref={unavailablePanelRef}>
                        {services?.filter((item) => (!item?.serviceability_info?.is_serviceable || !item.is_serviceable)).length === 0 ? (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        ) : (
                            <Space direction="vertical" style={{ width: '100%' }}>
                                {renderAllOptions()}
                                {renderUnavailableServices()}
                            </Space>
                        )}
                    </div>
                </Panel>
            </Collapse>
        );
    };

    if (loading) {
        return (
            <Loader zIndex={10} />
        );
    }

    return (
        <div className={classes.servicesMain}>
            {renderServices()}
        </div>
    );
};

const mapStateToProps = (state: ReduxStore) => {
    const customerPortalConfig:CustomerPortalConfig = state?.master?.config?.customer_portal_config;
    const customFieldsList = state?.master?.config?.custom_fields_list_for_form || [];
    const allowedConsignmentFormType = state?.master?.config?.customer_portal_ops_config
        ?.consignment_flow_type_config?.config_value?.allowed_consignment_form_type_customer_portal || [];
    const softdataCreationUnifiedFlow = state?.master?.parts_to_show?.softdata_creation_unified_flow_enabled;
    return {
        softdataCreationUnifiedFlow,
        allowedConsignmentFormType,
        customFieldsList,
        showRiskSurchargeType: customerPortalConfig?.show_risk_surcharge_type,
    };
};

const hocConfig: HocOptions = {
    connectRedux: {
        useRedux: true,
        mapStateToProps,
    },
    connectJss: {
        useJss: true,
        styleSheet: scheduleStepStyles,
    },
    connectRouter: true,
    connectTranslession: true,
};

export default GenericHoc(hocConfig)(ServiceTypeSelectorFieldErrorVisibility);
